import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useOrders from "../../hooks/useOrders";
import { Button } from "src/business-types/grocery/components/Button";
import iconMap from "../../../../assets/icons/Icons";
import {
  getOrder,
  patchOrder,
} from "../../../../shared/services/services";
import LoadingScreen from "../../../../shared/components/LoadingScreen";
import { useUser } from "../../components/UserContext";
import { notifySuccess } from "../../../../assets/toastService";
import Print, { PrintData } from "../../../../print-configuration/PrintConfigration";
import PopupModules from "src/features/auth/components/PopupModules";
import PaymentPopup from "./PaymentPopup";
import {
  ThemeBasedColorHeader,
  ThemeBasedColorMenubar,
} from "src/shared/modules/profile/theme/themeCheacker";
import Goback from "../../components/Goback";
import OrderSidePanal from "./OrderSidePanal";
import PaymentReverse from "./PaymentReverse";
import { errorHandling } from "src/shared/utils/errorHandling";
import InputBox from "../../components/InputBox";
import useUOM from "src/shared/hooks/useUOM";
import useProductsIndexDB from "../../hooks/useProductIndexDB";
import useCategory from "../../hooks/useCategory";
import ReturnReplace, { changeDetails } from "./ReturnReplace";
import { Order, OrderedProduct, ProductReturnItem } from "src/shared/types";
import { formatDate, formatTime } from "src/business-types/utils/dateTimeUtils";
import { processCardPayment } from "src/paymentGatway/useCardPayment";

// ────────────────────────────────────────────────────────────────────────────

function SpecifiedOrderDisplay() {
  const { user, userConfigration } = useUser();
  const { orderid } = useParams();
  const { orderData, loadingOrder, setOrderData, putOrderData } = useOrders("", orderid);

  const order: Order = orderData?.[0] ?? orderData;

  // Shorthand accessors for nested data
  const transaction = order?.orders?.[0];
  const currentStatus = transaction?.status ?? "";

  const [orderStatus, setOrderStatus] = useState<string>(currentStatus);
  const [orderChange, setOrderChange] = useState<Order | undefined>();
  const [editableProducts, setEditableProducts] = useState<any[]>([]);
  const [lastOrderData, setLastOrderData] = useState<Order | undefined>();
  const [loading] = useState<boolean>(false);

  const [payment, setPayment] = useState<boolean>(false);
  const [changeConfimation, setChangeConfimation] = useState<boolean>(false);
  const [printData, setPrintData] = useState<string>("");
  const [history, setHistory] = useState<boolean>(false);
  const [payOnly, setPayOnly] = useState<boolean>(false);
  const [reversedPayment, setReceivedPayment] = useState<boolean>(false);
  const [enabileEdit, setEnableEdit] = useState<boolean>(false);
  const [returnReplace, setReturnReplace] = useState<boolean>(false);

  const { uom } = useUOM();
  const { product, loadingPro, productSync } = useProductsIndexDB();
  const { category } = useCategory();
  const [returnProduct, setReturnProduct] = useState<ProductReturnItem[]>([]);
  const EastIcon = iconMap["EastIcon"];
  const DeleteIcon = iconMap["Delete"];
  // ── get Return handlers ─────────────────────────────────────────────────────

  useEffect(() => {
    if (!order) return;
    const returnItems = order?.orderDetails?.filter((detail) => detail.paymentSource === "RETURN")
    const returnedProducts = returnItems?.flatMap((detail) => detail.products) ?? [];
    setReturnProduct(returnedProducts)
  }, [order]);



  // ── Sync editable products from order ──────────────────────────────────────
  useEffect(() => {
    if (!order) return;
    setOrderChange(order);
    setEditableProducts(order.orderDetails?.[0]?.products ?? []);
  }, [order]);

  // ── Reformat products when edit mode or deps change ───────────────────────
  const calculateLineTotal = (item: OrderedProduct): number => {
    const base = (Number(item.quantity ?? 0) * Number(item.unitPrice ?? 0) + Number(item.addOns?.reduce((acc, addOn) => acc + (Number(addOn.amount) || 0), 0) ?? 0));

    return base;
  };

  useEffect(() => {
    const products = orderChange?.orderDetails?.[0]?.products;
    if (!products?.length || !product?.length || !uom?.length) return;

    const formatted: any[] = products.map((item) => ({
      ...item,
      quantity: Number(item.quantity ?? 0),
      unitPrice: Number(item.unitPrice ?? 0),
      notes: (item.notes ?? []).map((n: any, i: number) => ({
        sNo: n.sNo ?? i + 1,
        text: n.text ?? "",
        amount: Number(n.amount ?? 0),
      })),
      service: (item.service ?? []).map((s: any, i: number) => ({
        sNo: s.sNo ?? i + 1,
        text: s.text ?? "",
        amount: Number(s.amount ?? 0),
      })),
      get lineTotal() {
        return calculateLineTotal(this as OrderedProduct);
      },
    }));

    setEditableProducts(formatted);
  }, [orderChange?.orderDetails, product, uom, enabileEdit]);

  // ── Payment calculations ──────────────────────────────────────────────────
  const finalAmount = Number(transaction?.finalAmount ?? 0);

  const received =
    order?.paymentHistory?.reduce(
      (total, item) => total + Number(item?.receivedAmount ?? 0),
      0
    ) ?? 0;

  const alreadyPaid = received; // same source of truth
  // paymentHistory in new schema has no adjustAmount — default to 0
  const adj = 0;
  const remaining = finalAmount - alreadyPaid - adj;

  const remainingPrice = (): string =>
    Math.max(0, finalAmount - received).toFixed(2);

  // ── Status helpers ────────────────────────────────────────────────────────
  const statusChange = (value: string) => {
    setChangeConfimation(true);
    setOrderStatus(value);
  };

  const cancel = () => {
    setChangeConfimation(false);
    setPayment(false);
    setOrderStatus(currentStatus);
    setReceivedPayment(false);
  };

  // ── API calls and side effects ──  //
  const callApi = async (data?: any, openPrint?: string) => {
    const statusUpdate = data ?? { orderStatus: orderStatus, statusChange: true };
    if (data?.payments.paymentMethod === "Card" && Boolean(userConfigration?.cardPayment) === true) {
      await processCardPayment({
        amount: data?.payments?.receivedAmount,
        description: `Order payment for ${order?.customerDetails?.phones?.[0]?.phone || "Unknown Customer"}`,
        onSuccess: async (paymentDetails) => {
          const paysucess = {
            ...statusUpdate,
            paymentDetails,
          };
          patchOrder(order.orderId, paysucess)
            .then(() => {
              notifySuccess("Order updated successfully");
              reCallGetOrder(openPrint);
              cancel();
            })
            .catch((error) => errorHandling(error));
        },
      });
      return
    }
    patchOrder(order.orderId, statusUpdate)
      .then(() => {
        notifySuccess("Order updated successfully");
        reCallGetOrder(openPrint);
        cancel();
      })
      .catch((error) => errorHandling(error));
  };

  const reCallGetOrder = (openPrint?: string) => {
    getOrder(orderid).then((res) => {
      const fetched: Order = res.data.result;
      setOrderData([fetched]);
      if (openPrint) {
        setHistory(true);
        setLastOrderData(fetched);
        setPrintData(fetched._id.$oid);
        setPayOnly(false);
      }
    });
  };

  const confirm = () => {
    if (["cancel", "return & refund"].includes(orderStatus?.toLowerCase())) {
      received === 0 ? callApi() : setReceivedPayment(true);
    } else if (orderStatus !== "Delivered" || remaining <= 0) {
      callApi();
    } else {
      // Delivered
      if (remaining >= 0) {
        callApi();
      } else {
        setChangeConfimation(false);
        setPayment(true);
      }
    }
  };

  // ── Print data ────────────────────────────────────────────────────────────
  const resvedAmount = order?.paymentHistory?.reduce((sum, item) => sum + Number(item?.receivedAmount ?? 0), 0) || 0
  const balance = (order?.orders?.[0]?.finalAmount ?? 0) - resvedAmount;
  const customer = order?.customerDetails;

  const PrintData: PrintData = {
    CustomerName: [
      customer?.firstName,
      customer?.middleName,
      customer?.lastName,
    ]
      .filter(Boolean)
      .join(" "),

    CustomerPhon: order?.customerDetails?.phones?.[0]?.phone || "",

    OrderId: lastOrderData?.orderId || "",
    OrderStatus: order?.orders?.[0]?.status || "",

    discount: Number(order?.orders?.[0]?.discount || 0),
    deliveryFee: Number(order?.orders?.[0]?.deliveryFee || 0),

    Date: formatDate(order?.orders?.[0]?.createdOn?.$date),
    Time: formatTime(order?.orders?.[0]?.createdOn?.$date),

    Products:
      order?.orderDetails?.[0]?.products?.map((p) => ({
        productName: p?.name,
        arabicword: p?.arabicName,
        amount: Number(p?.unitPrice || 0),
        quantity: Number(p?.quantity || 0),
        totalPrice: Number(p?.lineTotal || 0),
        uom: p?.uom || "",
      })) || [],

    Paid:
      order?.paymentHistory?.reduce(
        (sum, item) => sum + Number(item?.receivedAmount ?? 0),
        0
      ) || 0,

    unpaid: balance > 0 ? balance : 0,

    returnAmount:
      lastOrderData?.paymentHistory?.[0]?.changeReturned || 0,

    balance: Number(balance?.toFixed?.(2) || 0),

    OSB: order?.totalOutstandingBalance || 0,

    paymentMethod:
      lastOrderData?.paymentHistory?.[0]?.paymentMethod || "",

    total: Number(order?.orders?.[0]?.orderAmount || 0),

    subtotal: Number(order?.orders?.[0]?.finalAmount || 0),

    clientId: order?.customerDetails?.refId?.toString() || "",

    billNo: Number(order?.orders?.[0]?.billNumber || 0),

    resvedAmount: Number(resvedAmount || 0),

    balanceReturned:
      lastOrderData?.paymentHistory?.[0]?.changeReturned || 0,

    businessType: user?.businessType,

    payMentHistory:
      lastOrderData?.paymentHistory?.map((p) => ({
        Paymentmethod: p?.paymentMethod || "",
        ReceivedAmount: Number(p?.receivedAmount || 0),
        date: formatDate(p?.createdOn?.$date),
        payment_on: formatTime(p?.paidOn?.$date),
        balance: Number(p?.amountDue || 0),
        paidAmount: Number(p?.receivedAmount || 0),
        balanceReturned: Number(p?.changeReturned || 0),
      })) || [],

    payMentHistoryAvabile:
      (lastOrderData?.paymentHistory?.length || 0) > 0,
  };

  // ── Product edit handlers ─────────────────────────────────────────────────
  const handleProductChange = (
    index: number,
    field: "quantity" | "unitPrice",
    value: string
  ) => {
    setEditableProducts((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        const updated = { ...item, [field]: Number(value) || 0 };
        return { ...updated, lineTotal: calculateLineTotal(updated) };
      })
    );
  };

  const handleServiceChange = (
    productIndex: number,
    serviceIndex: number,
    field: "text" | "amount",
    value: any
  ) => {
    setEditableProducts((prev) =>
      prev.map((item, i) => {
        if (i !== productIndex) return item;
        const updatedServices = [...(item.service ?? [])];
        updatedServices[serviceIndex] = {
          ...updatedServices[serviceIndex],
          [field]: field === "amount" ? Number(value) : value,
        };
        return {
          ...item,
          service: updatedServices,
          lineTotal: calculateLineTotal({ ...item, service: updatedServices }),
        };
      })
    );
  };

  const handleAddService = (productIndex: number) => {
    setEditableProducts((prev) =>
      prev.map((item, i) => {
        if (i !== productIndex) return item;
        const newService = {
          sNo: String((item.service?.length ?? 0) + 1),
          text: "",
          amount: 0,
        };
        return { ...item, service: [...(item.service ?? []), newService] };
      })
    );
  };

  const handleDeleteService = (productIndex: number, serviceIndex: number) => {
    setEditableProducts((prev) =>
      prev.map((item, i) => {
        if (i !== productIndex) return item;
        const updatedServices = [...(item.service ?? [])];
        updatedServices.splice(serviceIndex, 1);
        return {
          ...item,
          service: updatedServices,
          lineTotal: calculateLineTotal({ ...item, service: updatedServices }),
        };
      })
    );
  };

  const addProductToOrder = (productId: string) => {
    if (productId === "other") return;
    const selected = product.find(
      (p) => (p._id?.$oid ?? p._id) === productId
    );
    if (!selected) return;

    setEditableProducts((prev) => {
      const existingIndex = prev.findIndex((p) => p.productId === productId);
      if (existingIndex !== -1) {
        const updated = [...prev];
        const qty = Number(updated[existingIndex].quantity) + 1;
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: qty,
          lineTotal: qty * Number(updated[existingIndex].unitPrice),
        };
        return updated;
      }

      const newProduct: OrderedProduct = {
        productId,
        name: selected.productName,
        arabicName: selected.arabicName ?? "",
        quantity: 1,
        unitPrice: Number(selected.amount),
        lineTotal: Number(selected.amount),
        notes: [],
        service: [],
        type: selected.type ?? "",
      };
      return [...prev, newProduct];
    });
  };

  // ── Order modify flow ─────────────────────────────────────────────────────
  const orderModify = (
    type: "confirm" | "modify",
    data?: changeDetails
  ) => {
    if (type === "confirm") {
      setReturnReplace(true);
      return;
    }
    if (type === "modify" && data) {
      const putData = {
        ...(data.difference > 0 && {
          ...orderChange,
          orders: [
            {
              ...(transaction ?? {}),
              remainingAmount: data.balance < 0 ? 0 : data.balance,
              outstandingBalance: Number(order?.totalOutstandingBalance ?? 0) + data.difference,
            },
          ],
          id: order.customerDetails,
          paymentHistory: [
            ...(order.paymentHistory ?? []),
            {
              paymentMethod: data.paymentType,
              receivedAmount: data.amount,
              balance: data.balance < 0 ? 0 : data.balance,
              paidOn: { $date: new Date().toISOString() },
            },
          ],
          payments: [
            { paymentMethod: data.paymentType, receivedAmount: data.amount },
          ],
        }),
        ...(data.difference < 0 && {
          ...orderChange,
          adjust_type:
            data.accountAdjesment === "refund" ? "refund" : "deduct_from_due",
          paymentHistory: [
            ...(order.paymentHistory ?? []),
            {
              paymentMethod: data.accountAdjesment,
              receivedAmount: data.difference,
              balance: 0,
              paidOn: { $date: new Date().toISOString() },
            },
          ],
        }),
        ...(data.difference === 0 && { ...orderChange }),
      };
      putOrderData(order?.orderId, putData).then(() => setEnableEdit(false));
    }
  };

  // ── Guard ─────────────────────────────────────────────────────────────────
  if (loading || loadingOrder) return <LoadingScreen />;

  // ── Order passed to children with editable products merged in ─────────────
  const orderWithEdits: Order =
    editableProducts.length > 0
      ? {
        ...order,
        orderDetails: [
          { ...order.orderDetails[0], products: editableProducts },
        ],
      }
      : order;
  const toBoolean = (value: any) => value === true || value === "true";
  const orderEditingEnabled = toBoolean(userConfigration?.orderEditingEnabled)
  console.log(orderEditingEnabled, "orderEditingEnabled")

  return (
    <div className="w-full h-full overflow-hidden p-1">
      <div className="flex flex-col md:flex-row-reverse h-full w-full p-1 gap-2 overflow-auto">
        <OrderSidePanal
          order={orderWithEdits}
          received={received}
          remainingPrice={remainingPrice()}
          statusChange={statusChange}
          orderStatus={orderStatus}
          setPayment={setPayment}
          setPayOnly={setPayOnly}
          setPrintData={setPrintData}
          setLastOrderData={setLastOrderData}
          setHistory={setHistory}
          osb={order?.totalOutstandingBalance ?? 0}
          editData={enabileEdit}
          setEditData={setEnableEdit}
          setOrderChange={(data: Order) => {
            setOrderChange({
              ...data,
              orderDetails: [
                {
                  ...data.orderDetails[0],
                  products: editableProducts,
                },
              ],
            });
            orderModify("confirm");
          }}
          sendToOrder={addProductToOrder}
          product={product}
          category={category}
          productSync={productSync}
          loadingPro={loadingPro}
        />

        {/* ── Products Table ───────────────────────────────────────────── */}
        <div
          className={`w-full lg:w-4/6 h-full border ${ThemeBasedColorMenubar()} rounded-xl p-2 shadow-inner flex flex-col overflow-auto`}
        >
          <div className="flex items-center gap-5 justify-between">
            <div className="flex gap-4">
              <Goback />
              <h1 className="text-left text-2xl font-semibold mb-2">
                Order Items
              </h1>
              {orderEditingEnabled && (enabileEdit ? <button onClick={() => setEnableEdit(false)}>❌</button> : <button onClick={() => setEnableEdit(true)}>✏️</button>)}
            </div>
          </div>

          {/* Header */}
          <div
            className={`hidden sm:flex w-full justify-between gap-2 ${ThemeBasedColorHeader()} p-2 rounded-t shadow-sm mb-1 text-sm font-medium`}
          >
            {[
              "S.No",
              "Product Name / 2nd Name",
              "Price (QR)",
              "Qty",
              "UOM",
              "Return Qty",
              "Total (QR)",
            ].map((col, i) => (
              <div
                key={col}
                className={[
                  "font-semibold",
                  i === 0
                    ? "w-1/12 text-center"
                    : i === 1
                      ? "w-3/12"
                      : i === 5
                        ? "w-1/12 text-start"
                        : "w-2/12 text-end",
                ].join(" ")}
              >
                {col}
              </div>
            ))}
          </div>

          {/* Rows */}
          {editableProducts.map((item, index) => (
            <div key={index}>
              <div className="w-full flex flex-col sm:flex-row justify-between gap-2 bg-white p-2 rounded shadow-sm mb-1 text-sm">
                {/* S.No */}
                <div className="sm:w-1/12 text-center font-semibold">
                  {index + 1}
                </div>

                {/* Name */}
                <div className="sm:w-3/12">
                  {item.name}
                  {item.arabicName ? ` / ${item.arabicName}` : ""}
                </div>

                {/* Unit Price */}
                {enabileEdit ? (
                  <InputBox
                    value={item.unitPrice}
                    className="sm:w-2/12 text-right"
                    onChange={(e: any) =>
                      handleProductChange(index, "unitPrice", e.target.value)
                    }
                  />
                ) : (
                  <div className="sm:w-2/12 text-end">
                    {Number(item.unitPrice).toFixed(2)}
                  </div>
                )}

                {/* Qty */}
                {enabileEdit ? (
                  <InputBox
                    value={item.quantity}
                    className="sm:w-2/12 text-right"
                    onChange={(e: any) =>
                      handleProductChange(index, "quantity", e.target.value)
                    }
                  />
                ) : (
                  <div className="sm:w-2/12 text-end font-semibold">
                    {item.quantity}
                  </div>
                )}
                {/* UOM */}
                <div className="sm:w-1/12 text-center font-semibold">
                  {item.uom}
                </div>

                {/* Return Qty — returnHistory not in new schema, default 0 */}
                <div className="sm:w-1/12 text-center font-semibold text-orange-500">
                  {Math.abs(
                    returnProduct
                      .filter((p) => p.productId === item.productId)
                      .reduce((sum, p) => sum + p.quantity, 0)
                  )}
                </div>

                {/* Line Total */}
                <div className="sm:w-2/12 text-end font-semibold">
                  {Number(item.lineTotal).toFixed(2)}
                </div>
              </div>

              {/* Services */}
              {(item.addOns ?? []).length > 0 && (
                <div className="mx-8 mb-2 flex flex-col items-end bg-gray-50 rounded-md border border-gray-200">
                  {(item.addOns ?? []).map((svc: any, sIndex: number) => (
                    <div
                      key={sIndex}
                      className="flex justify-between items-center px-3 py-1 border-b last:border-b-0 w-full gap-2"
                    >
                      <div className="flex items-center gap-2 text-gray-600 w-2/3">
                        <span className="text-gray-400">{sIndex + 1}.</span>
                        {enabileEdit ? (
                          <InputBox
                            value={svc.text}
                            placeholder="Add note..."
                            className="w-full"
                            onChange={(e: any) =>
                              handleServiceChange(index, sIndex, "text", e.target.value)
                            }
                          />
                        ) : (
                          <span>{svc.text || "—"}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {enabileEdit ? (
                          <InputBox
                            value={svc.amount}
                            className="w-20 text-right"
                            onChange={(e: any) =>
                              handleServiceChange(index, sIndex, "amount", e.target.value)
                            }
                          />
                        ) : (
                          <span className="font-medium text-gray-700">
                            {Number(svc.amount).toFixed(2)}
                          </span>
                        )}
                        {enabileEdit && (
                          <div
                            className="cursor-pointer text-red-500"
                            onClick={() => handleDeleteService(index, sIndex)}
                          >
                            <DeleteIcon />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* {enabileEdit && (
                <div className="mx-8 mb-2 flex justify-end">
                  <div
                    className="cursor-pointer text-blue-500 text-sm"
                    onClick={() => handleAddService(index)}
                  >
                    ➕ Alternation
                  </div>
                </div>
              )} */}
            </div>
          ))}
        </div>
      </div>

      {/* ── Status Change Popup ─────────────────────────────────────────── */}
      <PopupModules
        isOpen={changeConfimation}
        onClose={cancel}
        title="Status Changing Confirmation"
      >
        <div>
          <div className="text-center text-gray-700 mb-6 flex justify-center items-center gap-2">
            <p>
              <span className="font-medium">From:</span>{" "}
              <b className="text-yellow-600">{currentStatus}</b>
            </p>
            <EastIcon />
            <p>
              <span className="font-medium">To:</span>{" "}
              <b
                className={
                  ["cancel", "return & refund"].includes(
                    orderStatus?.toLowerCase()
                  )
                    ? "text-red-600"
                    : "text-green-600"
                }
              >
                {orderStatus}
              </b>
            </p>
          </div>
          <div className="flex justify-center gap-4">
            <Button label="Cancel" variant="cancel" onClick={cancel} />
            <Button label="Confirm" onClick={confirm} />
          </div>
        </div>
      </PopupModules>

      <PaymentPopup
        totalSOB={order.totalOutstandingBalance ?? 0}
        cancel={cancel}
        order={order}
        payment={payment}
        payOnly={payOnly}
        callApi={callApi}
        orderStatus={order?.orders?.[0]?.status || ""}
      />
      <Print PrintData={PrintData} username={""} openPrint={printData} />
      <PaymentReverse
        openCancel={reversedPayment}
        closePop={() => setReceivedPayment(false)}
        osb={order.totalOutstandingBalance ?? 0}
        order={order}
        received={received}
        close={() => {
          cancel();
          reCallGetOrder();
          setEnableEdit(false);
        }}
      />
      <ReturnReplace
        returnReplace={returnReplace}
        setReturnReplace={() => setReturnReplace(false)}
        osb={order.totalOutstandingBalance ?? 0}
        order={order}
        orderChange={orderChange}
        changeConfirm={(data) => orderModify("modify", data)}
      />
    </div>
  );
}

export default SpecifiedOrderDisplay;